import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, XCircle, RefreshCw, Truck, CheckCircle2, Mail } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ReturnsRefunds = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <main className="max-w-4xl mx-auto px-6 py-14" data-testid="returns-refunds-page">
      <section className="text-center mb-10">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight">Exchange Policy</h1>
        <div className="h-1 w-16 bg-[#22c55e] mx-auto mt-3" />
        <p className="text-gray-600 mt-4">Please read carefully before placing your order.</p>
      </section>

      {/* Headline banner */}
      <section className="bg-black text-white rounded-2xl p-6 md:p-8 mb-8 flex items-start gap-4">
        <AlertTriangle className="text-[#22c55e] shrink-0 mt-1" size={28} />
        <div>
          <h2 className="text-xl md:text-2xl font-black">Exchanges only — no returns, no refunds.</h2>
          <p className="text-white/80 text-sm md:text-base mt-2 leading-relaxed">
            Because each Feron piece is made in small batches, we don't offer refunds or product returns. What we do offer is a fair, hassle-free <b>size exchange</b> if the piece doesn't fit right.
          </p>
        </div>
      </section>

      {/* No refunds card */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="border-2 border-red-100 bg-red-50/50 rounded-xl p-5" data-testid="policy-no-refund">
          <div className="flex items-center gap-2 mb-3">
            <XCircle className="text-red-500" size={20} />
            <h3 className="font-black text-base">No Refunds</h3>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed">Once payment is complete the amount is not refundable, in full or in part, for any reason.</p>
        </div>
        <div className="border-2 border-red-100 bg-red-50/50 rounded-xl p-5" data-testid="policy-no-return">
          <div className="flex items-center gap-2 mb-3">
            <XCircle className="text-red-500" size={20} />
            <h3 className="font-black text-base">No Returns</h3>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed">We do not accept returns. The order is yours to keep once it is delivered.</p>
        </div>
      </div>

      {/* Exchange allowed card */}
      <div className="border-2 border-[#22c55e]/40 bg-[#22c55e]/5 rounded-xl p-6 md:p-8 mb-8" data-testid="policy-exchange">
        <div className="flex items-center gap-2 mb-4">
          <RefreshCw className="text-[#22c55e]" size={22} />
          <h3 className="font-black text-lg md:text-xl">Size exchanges — how it works</h3>
        </div>
        <ol className="space-y-4">
          {[
            { title: 'Email us within 7 days', body: 'Message feronstore@web.de with your order ID, the item you want to exchange, and the new size you need — within 7 days of delivery.' },
            { title: 'You ship it back', body: 'You are responsible for the one-way shipping cost of sending the item back to us. Use any traceable courier — untracked parcels are at your own risk.' },
            { title: 'We inspect', body: 'The piece must be unworn, unwashed, with the original tags attached. Any signs of wear disqualify the exchange.' },
            { title: 'We ship the new size', body: 'Once inspected, we ship the replacement size to you for free — subject to stock. If your requested size is unavailable, we\'ll offer you a store credit of equal value instead.' },
          ].map((s, i) => (
            <li key={s.title} className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-[#22c55e] text-black font-black text-sm flex items-center justify-center shrink-0">{i + 1}</div>
              <div>
                <p className="font-black text-sm">{s.title}</p>
                <p className="text-sm text-gray-700 mt-0.5 leading-relaxed">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Cost callout */}
      <div className="bg-neutral-50 border rounded-xl p-6 mb-8 flex items-start gap-4">
        <Truck className="text-[#22c55e] mt-1 shrink-0" size={22} />
        <div>
          <h3 className="font-black text-base">One-way shipping is on you</h3>
          <p className="text-sm text-gray-700 mt-1 leading-relaxed">
            The buyer bears the shipping cost of sending the piece back to us. We cover the shipping of the replacement size to you — that side is on us.
          </p>
        </div>
      </div>

      {/* Ineligible list */}
      <div className="mb-10">
        <h3 className="font-black text-lg mb-3 flex items-center gap-2"><CheckCircle2 size={18} className="text-[#22c55e]" /> Not eligible for exchange</h3>
        <ul className="space-y-2 text-sm text-gray-700">
          {[
            'Items that have been worn, washed, or altered.',
            'Items without their original tags or packaging.',
            'Items purchased with a discount coupon of 40% or more.',
            'Requests made more than 7 days after delivery.',
          ].map((r, i) => (
            <li key={r} className="flex gap-2"><XCircle size={16} className="text-red-500 mt-0.5 shrink-0" /><span>{r}</span></li>
          ))}
        </ul>
      </div>

      <div className="text-center bg-black text-white rounded-2xl p-8">
        <Mail className="text-[#22c55e] mx-auto mb-3" size={28} />
        <h3 className="font-black text-xl mb-2">Ready to start an exchange?</h3>
        <p className="text-white/70 mb-4 text-sm">Email us at <a href="mailto:feronstore@web.de" className="text-[#22c55e] underline">feronstore@web.de</a> with your order ID.</p>
        <Link to="/contact-us" className="inline-block bg-[#22c55e] hover:bg-[#16a34a] text-black font-black px-6 py-3 rounded-full tracking-wider transition" data-testid="returns-contact-cta">
          CONTACT US
        </Link>
      </div>
    </main>
    <Footer />
  </div>
);

export default ReturnsRefunds;
